/**
 * Client for the studio Instagram account field (tv-isee.2):
 * `GET /api/channel-accounts` lists the Instagram accounts linked in HookChat, and
 * `POST /api/intents/assign-channel-account` assigns one to a studio (releasing its
 * held DMs). Both are owner/georgina only; the server checks the login token.
 * The list throws on failure (React Query's contract); the intent never throws and
 * returns the same `IntentFailure` shape as `@/api/overrideIntents`.
 */
import { authHeaders, isSigningIn, whenSignedIn } from '@/api/persist';
import { intentErrorKind, type IntentFailure } from '@/api/intents';

export interface ChannelAccount {
  platform: 'instagram';
  accountExternalId: string;
  handle: string | null;
  status: 'connected' | 'disconnected';
  studioId: string | null;
}

export async function fetchChannelAccounts(): Promise<ChannelAccount[]> {
  if (isSigningIn()) await whenSignedIn();
  const res = await fetch('/api/channel-accounts', { headers: { ...authHeaders() } });
  if (!res.ok) throw new Error(`channel-accounts ${res.status}`);
  return ((await res.json()) as { accounts: ChannelAccount[] }).accounts;
}

export interface AssignChannelAccountPayload {
  platform: 'instagram';
  accountExternalId: string;
  studioId: string;
}

export type AssignChannelAccountResult = { ok: true; released: number } | IntentFailure;

export async function assignChannelAccountIntent(payload: AssignChannelAccountPayload): Promise<AssignChannelAccountResult> {
  if (isSigningIn()) await whenSignedIn();
  let res: Response;
  try {
    res = await fetch('/api/intents/assign-channel-account', {
      method: 'POST',
      headers: { 'content-type': 'application/json', ...authHeaders() },
      body: JSON.stringify(payload),
    });
  } catch {
    return { ok: false, kind: 'transient', status: 0, error: 'network' };
  }
  const body = (await res.json().catch(() => undefined)) as
    | { released?: unknown; error?: string; code?: string; message?: string }
    | undefined;
  if (res.ok) return { ok: true, released: typeof body?.released === 'number' ? body.released : 0 };
  return {
    ok: false,
    kind: intentErrorKind(res.status, body),
    status: res.status,
    error: body?.error ?? `http-${res.status}`,
    ...(typeof body?.code === 'string' ? { code: body.code } : {}),
    ...(body?.message ? { message: body.message } : {}),
  };
}
